import * as React from 'react';
import { addImageParams, getItemUrl, getMediumItemImageUrl, getPriceResultsUrl } from '@/utils/urls';
import { escapeQuotes } from '@/utils/escapeQuotes';
import { getBaseUrl } from '@/redux/modules/config';
import { getCatalogEndTime } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getCategoryLevels } from '@/utils/categoryGrabber';
import { getISODateTime } from '@liveauctioneers/hammer-ui-core/utils/dateTimeFormat';
import { getItemFacets } from '@/redux/modules/itemFacets';
import {
    getItemFirstImageUrl,
    getItemSummaries,
    getItemSummary,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { getSellerName } from '@/redux/modules/seller';
import { getSellerRatingById } from '@/redux/modules/sellerRatings';
import { getUpcomingSalesItemIds } from '@/redux/modules/priceResults/priceResults.selectors';
import { Rating } from '@/types/Rating';
import { safelyEscapeText } from '@/utils/safelyRender';
import { slugToTitleCase } from '@/utils/strings';
import { useAppSelector } from '@/redux/hooks';
import PageHeaderMeta from '@liveauctioneers/caterwaul-components/lib/PageHeaderMeta/PageHeaderMeta';
import useAddNotificationCountToTitle from '@/hooks/useAddNotificationCountToTitle';

const IMAGE_MAX_DIMENSION = 282;
const IMAGE_QUALITY_LEVEL = 95;

type Props = {
    itemId?: number;
    slug: string;
};

const PriceResultsPageHeaderMeta = ({ itemId, slug }: Props) => {
    const {
        catalogId,
        catalogStatus,
        imageVersion,
        photos = [],
        saleStartTs = Date.now() / 1000,
        sellerId,
        sellerName,
        shortDescription: itemDescription,
        title: itemTitle = '',
    } = useAppSelector((state) => getItemSummary(state, itemId));
    const baseUrl = useAppSelector((state) => getBaseUrl(state));
    const { categories } = useAppSelector((state) => getItemFacets(state, itemId));
    const name = useAppSelector((state) => getSellerName(state, sellerId));
    const houseRating: Rating = useAppSelector((state) => getSellerRatingById(state, sellerId));
    const saleEndTs = useAppSelector((state) => getCatalogEndTime(state, catalogId)) || Date.now() / 1000;

    const safeItemTitle = slugToTitleCase(safelyEscapeText(itemTitle.trim()));

    // replace all whitespace with single spaces to avoid excess length
    const safeItemDescription = safelyEscapeText(itemDescription);

    const description =
        safeItemDescription?.length > 120 ? `${safeItemDescription.substring(0, 119)}` : `${safeItemDescription}.`;

    const safeDescription = safelyEscapeText(description);
    const url = useAppSelector((state) => getItemFirstImageUrl(state, itemId));

    const imageUrl = getMediumItemImageUrl(sellerId, catalogId, itemId, photos[0], imageVersion);
    const title = useAddNotificationCountToTitle(safelyEscapeText(safeItemTitle));
    const canonical = baseUrl + getPriceResultsUrl(slug);
    const isNotPublished = !['done', 'live', 'online'].includes(catalogStatus);

    const mainImageUrlHighDpi = addImageParams({
        height: IMAGE_MAX_DIMENSION,
        quality: IMAGE_QUALITY_LEVEL,
        url,
        width: IMAGE_MAX_DIMENSION * 2,
    });

    const getLdMarkup = (data: any) => {
        return <script type="application/ld+json">{JSON.stringify(data)}</script>;
    };

    const upcomingSalesItemIds = useAppSelector(getUpcomingSalesItemIds);
    const items = useAppSelector((state) => getItemSummaries(state, upcomingSalesItemIds));

    const getLdData = React.useCallback(() => {
        const itemListData = {
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            itemListElement: items.map((item, index) => {
                const itemImageUrl = getMediumItemImageUrl(
                    item.sellerId,
                    item.catalogId,
                    item.itemId,
                    item.photos[0],
                    item.imageVersion
                );

                return {
                    '@type': 'ListItem',
                    item: {
                        '@type': 'Product',
                        description: safelyEscapeText(item.shortDescription),
                        image: {
                            '@type': 'ImageObject',
                            author: {
                                '@type': 'Organization',
                                name: 'LiveAuctioneers',
                                url: 'https://www.liveauctioneers.com',
                            },
                            contentUrl: itemImageUrl,
                            copyrightHolder: 'LiveAuctioneers',
                            copyrightNotice: 'LiveAuctioneers',
                            creator: {
                                '@type': 'Organization',
                                name: 'LiveAuctioneers',
                                url: 'https://www.liveauctioneers.com',
                            },
                            creditText: escapeQuotes(item.sellerName || 'LiveAuctioneers'),
                            description: safelyEscapeText(item.shortDescription),
                            fileFormat: 'image/jpeg',
                            height: IMAGE_MAX_DIMENSION,
                            mainEntityOfPage: `${baseUrl}${getItemUrl(item.itemId, safelyEscapeText(item.title))}`,
                            name: `${escapeQuotes(item.title)} main image`,
                            representativeOfPage: true,
                            sourceOrganization: 'LiveAuctioneers',
                            thumbnailUrl: itemImageUrl,
                            uploadDate: getISODateTime(new Date(saleStartTs * 1000), { short: true }),
                            URL: itemImageUrl,
                            width: IMAGE_MAX_DIMENSION,
                        },
                        name: safelyEscapeText(item.title),
                        offers: {
                            '@type': 'Offer',
                            availability:
                                item.catalogStatus === 'online'
                                    ? 'https://schema.org/InStock'
                                    : 'https://schema.org/OutOfStock',
                            price: (item.leadingBid || item.startPrice || 0).toString(),
                            priceCurrency: 'USD',
                            seller: {
                                '@type': 'Organization',
                                name: item.sellerName,
                                url: `${baseUrl}/auctioneer/${item.sellerId}/${name}/`,
                                ...(houseRating &&
                                    houseRating.overall && {
                                        aggregateRating: {
                                            '@type': 'AggregateRating',
                                            ratingValue: houseRating.overall.toFixed(1),
                                            reviewCount: houseRating.totalReviews,
                                        },
                                    }),
                            },
                            url: `${baseUrl}${getItemUrl(item.itemId, safelyEscapeText(item.title))}`,
                            validFrom: getISODateTime(new Date(saleStartTs * 1000), { short: true }),
                            validThrough: getISODateTime(new Date(saleEndTs * 1000), { short: true }),
                        },
                        sku: item.itemId.toString(),
                    },
                    position: index + 1,
                };
            }),
        };

        const breadcrumbData = {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [],
        };

        return [breadcrumbData, itemListData];
    }, [baseUrl, houseRating, items, name, saleEndTs, saleStartTs]);

    const getLdBreadcrumbs = React.useCallback(
        (categories: any[], currentUrl: string, title: string) => {
            if (!categories || !categories.length) {
                // Return default breadcrumb with just Home
                const [breadcrumbData] = getLdData();
                breadcrumbData.itemListElement.push({
                    '@type': 'ListItem',
                    item: {
                        '@id': `${baseUrl}`,
                        name: 'Home',
                    },
                    position: 0,
                });
                return getLdMarkup(breadcrumbData);
            }

            let categoryCount = 0;
            const [breadcrumbData] = getLdData();
            breadcrumbData.itemListElement.push({
                '@type': 'ListItem',
                item: {
                    '@id': `${baseUrl}`,
                    name: 'Home',
                },
                position: categoryCount++,
            });
            const categoryLevels = getCategoryLevels(categories[0]);
            while (categoryCount <= categoryLevels.length) {
                breadcrumbData.itemListElement.push({
                    '@type': 'ListItem',
                    item: {
                        '@id': `${baseUrl}/c/${
                            categoryLevels[categoryCount - 1].categoryUrl
                        }/${categoryLevels[categoryCount - 1].categoryId}/`,
                        name: categoryLevels[categoryCount - 1].categoryName,
                    },
                    position: categoryCount++,
                });
            }
            breadcrumbData.itemListElement.push({
                '@type': 'ListItem',
                item: {
                    '@id': currentUrl,
                    name: escapeQuotes(title),
                },
                position: categoryCount++,
            });
            return getLdMarkup(breadcrumbData);
        },
        [baseUrl, getLdData]
    );

    const ldBreadcrumbs = getLdBreadcrumbs(categories, canonical, safeItemTitle);

    return (
        <PageHeaderMeta
            canonical={canonical}
            description={safeDescription}
            imageUrl={imageUrl}
            title={title}
        >
            {isNotPublished && (
                <meta
                    content="noindex, nofollow"
                    name="robots"
                />
            )}
            {ldBreadcrumbs}
            {getLdMarkup(getLdData()[1])}
            <meta property="og:image:height" />
            <meta property="og:image:width" />
            <meta
                content="LiveAuctioneers"
                property="og:site_name"
            />
            <script type="application/ld+json">
                {`[{
                        "@context": "https://schema.org",
                        "@type": "ImageObject",
                        "author": {
                            "@type": "Organization",
                            "name": "LiveAuctioneers",
                            "url": "https://www.liveauctioneers.com"
                        },
                        "copyrightHolder": "LiveAuctioneers",
                        "creditText": "${escapeQuotes(sellerName || 'LiveAuctioneers')}",
                        "creator": {
                            "@type": "Organization",
                            "name": "LiveAuctioneers",
                            "url": "https://www.liveauctioneers.com"
                        },
                        "mainEntityOfPage": "${canonical}",
                        "copyrightNotice": "LiveAuctioneers",
                        "contentUrl": "${mainImageUrlHighDpi}",
                        "thumbnailUrl": "${mainImageUrlHighDpi}",
                        "URL": "${mainImageUrlHighDpi}",
                        "description": "${escapeQuotes(safeDescription)}",
                        "fileFormat": "image/jpeg",
                        "name": "${escapeQuotes(itemTitle)} main image",
                        "sourceOrganization": "LiveAuctioneers",
                        "uploadDate": "${getISODateTime(new Date(saleStartTs * 1000), { short: true })}",
                        "representativeOfPage": true,
                        "width": 282,
                        "height": 282
                }]`}
            </script>
        </PageHeaderMeta>
    );
};

export default React.memo<Props>(PriceResultsPageHeaderMeta);
